import clsx from "clsx";
import { compactNumber } from "../util/format.js";
import { StatDelta, type StatDeltaUnit } from "../StatDelta/StatDelta.js";
import styles from "./DarkStatTile.module.scss";

/** A change to render under a tile's value — the number AND the unit it means something in. They travel
 *  together for the reason {@link StatDeltaUnit} gives: the unit belongs to the datum, not to the tile. */
export interface DarkStatChange {
  value: number | null | undefined;
  unit?: StatDeltaUnit;
}

export interface DarkStatTileProps {
  /** The uppercased mono label (e.g. "Reach · 30d"). */
  label: string;
  /** The metric. **This component OWNS the null → `—` contract** — a null/undefined/non-finite value renders
   *  `—`, NEVER a fabricated number (leak-test contract).*/
  value: number | null | undefined;
  /** A glyph before the value (e.g. `$` for spend). */
  prefix?: string;
  /** A glyph after the value (e.g. `/100` for a popularity score). */
  suffix?: string;
  /** Render the exact grouped number (`1,234`) instead of the compact `1.2k` form. */
  exact?: boolean;
  /** Loading flash — swaps the value for a shimmer placeholder (the tile chrome still reserves its space). */
  loading?: boolean;
   /**
   * The change, rendered UNDER the value as a bare tinted delta (up=green, down=red, flat=muted). Carries its
   * own unit — a percent for a level, the flat difference for a running total or a bounded index (see
   * {@link StatDeltaUnit}).
   *
   * Null/undefined — or a null `value` inside it — renders nothing at all: the tile shows its value with no
   * change line, which stays the ordinary case, because a delta needs two adjacent readings.
   */
  delta?: DarkStatChange | null;
  /**
   * The type scale.
   *
   * - `md` (default) — an 18px value. The per-platform chips on the Content reach hero.
   * - `lg` — the reference's HERO scale: a 32px value over a 10px label (`roster-shared.css:553-554`
   *   runs 30px/10px; 32 is the nearest rung). For a hero whose stat row IS the card.
   *
   * Opt-in so no existing surface moves — this tile is shared by Music, Content and Marketing.
   */
  size?: "md" | "lg";
  /**
   * The tile's own chrome — the semitransparent fill, the hairline and the radius that make it read as a
   * chip on the hero.
   *
   * - `tile` (default) — the boxed chip. Every surface that had one before this prop existed.
   * - `none` — label + value + delta as bare type on the hero, with no fill, no border and no inset padding,
   *   so the stat row sits on the hero's own gutter. ART-98: the Streaming hero's chips read as "pills around
   *   the numbers" and were dropped.
   *
   * Opt-in for the same reason `size` is: this tile is shared by Music, Marketing, Content and Releases, and
   * unboxing it here must not unbox theirs.
   */
  chrome?: "tile" | "none";
  /**
   * A second, already-formatted reading shown after the value on the same line, as `value · secondary`.
   *
   * For a tile whose metric is genuinely a PAIR rather than two metrics — Marketing's "Clicks / CTR · 30d"
   * reads "6,420 · 1.25%", one question ("did the impressions convert?") answered with a count and its rate.
   * It rides beside `value` rather than replacing it so the tile keeps owning the null → `—` contract: an
   * absent count is still `—`, and the rate is never shown hanging off nothing.
   */
  secondary?: string | null;
  /**
   * The line shown UNDER an empty value, in place of the delta — WHY there is no number.
   *
   * `—` alone cannot distinguish "you have not connected an ad account" from "you connected one and spent
   * nothing last month". The first has something for the band to do and the second does not, and pointing
   * them at a connect step they already completed is the failure the connections card already made once
   * (`last_status == "error"`). A tile with a real value ignores this entirely.
   */
  emptySub?: string | null;
  /** A context line UNDER a present value ("1 in progress", "of $5,000") — the tile's own reading of what the
   *  number means. Ignored while the value is empty (that line is {@link DarkStatTileProps.emptySub}). */
  sub?: string | null;
  /** Makes the whole tile a button — a tile that opens the place its number comes from (the release card's
   *  tiles open that release's tab, ART-335). The label becomes part of the button's name. */
  onClick?: () => void;
  className?: string;
}

/**
 * A KPI stat tile for the DARK spotlight surface (FE-SH-02). The on-light {@link StatBlock} hardcodes
 * `--ac-color-ink` and is invisible on the inverse surface; this is its dark-safe sibling — the SAME null → `—`
 * leak contract, styled on the spotlight-tile token family. Extracted from Music's `HeroTile` +
 * Marketing's `DarkStat` (both hand-rolled the identical tile); consumed by Music / Marketing / Content heroes.
 */
export function DarkStatTile({
  label, value, prefix, suffix, exact, loading, delta, size = "md", chrome = "tile", secondary, emptySub, sub,
  onClick, className,
}: DarkStatTileProps) {
  const empty = value == null || !Number.isFinite(value);
  const Root = onClick ? "button" : "div";
  return (
    <Root
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={clsx(styles.tile, size === "lg" && styles.lg, chrome === "none" && styles.bare, onClick && styles.action, className)}
    >
      <span className={styles.label}>{label}</span>
      {loading ? (
        <span className={styles.skel} aria-hidden="true" />
      ) : (
        <span className={styles.value}>
          {empty ? (
            "—"
          ) : (
            <>
              {prefix}
              {exact ? value!.toLocaleString() : compactNumber(value!)}
              {suffix}
              {secondary ? ` · ${secondary}` : null}
            </>
          )}
        </span>
      )}
      {/* Gated on `empty` as well as on the delta itself: a percent change beside a `—` describes a value the
          tile is not showing, which reads as a number the band cannot find anywhere. */}
      {!loading && !empty && (
        <StatDelta value={delta?.value} unit={delta?.unit} tone="inverse" className={styles.delta} />
      )}
      {!loading && empty && emptySub ? <span className={styles.emptySub}>{emptySub}</span> : null}
      {!loading && !empty && sub ? <span className={styles.sub}>{sub}</span> : null}
    </Root>
  );
}
